import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Card, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
} from '@mui/material';
import {
  StraightenOutlined as MeasureIcon,
  ViewWeekOutlined as GrainIcon,
} from '@mui/icons-material';
import ScheduleService, { TimelineWeek } from '../../api/services/schedule.service';
import { describeError } from '../../api/services/payroll.service';
import PageBody from '../../components/ui/PageBody';
import PageHeader from '../../components/ui/PageHeader';
import {
  ChoiceFilter, DateRange, DateRangeFilter, FilterBar, RangePreset,
} from '../../components/ui/FilterBar';
import { EmptyState, LoadingState } from '../../components/ui/States';
import { LineChart, LineWeek } from '../payroll/reportCharts';
import { count, shortDate, weekdayShort } from '../payroll/payrollUI';
import SchedulePanel from './SchedulePanel';
import { isoDay, shiftWeek, weekEndingFor } from './scheduleMath';

// The sheet's "Capacity Plan & Graph": what happened, what is expected and
// what the roster can carry. Three lines and no more: expected is already
// last year's same week raised by the growth percent, so a fourth line for
// last year would only draw the same shape lower down.
//
// A week is named by the Saturday it starts on, in the chart and the table.
// Filters are the reports' bar: a date range, then what to measure and by
// what grain.

type Metric = 'stops' | 'packages';
type Grain = 'week' | 'day';
const DEFAULT_PRESET = 'around13';

const num = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', py: 0.5, px: 1.25 } as const;
const opt = (n: number | null | undefined) => (n === null || n === undefined ? '—' : count(n));
const weekStartOf = (weekEnding: string) => isoDay(new Date(Date.parse(`${weekEnding.slice(0, 10)}T00:00:00Z`) - 6 * 86400000));

/** Presets around this week, since the timeline runs both ways from now. */
function timelinePresets(thisWeek: string, weeks: TimelineWeek[]): RangePreset[] {
  if (!thisWeek || !weeks.length) return [];
  const span = (back: number, ahead: number) => ({ from: weekStartOf(shiftWeek(thisWeek, -back)), to: shiftWeek(thisWeek, ahead) });
  const year = thisWeek.slice(0, 4);
  return [
    { key: 'around8', label: '8 weeks either side', range: span(8, 8) },
    { key: 'around13', label: '13 weeks either side', range: span(13, 13) },
    { key: 'next13', label: 'Next 13 weeks', range: span(0, 12) },
    { key: 'last26', label: 'Last 26 weeks', range: span(25, 0) },
    { key: 'last52', label: 'Last 52 weeks', range: span(51, 0) },
    { key: 'year', label: `${year}`, range: { from: `${year}-01-01`, to: `${year}-12-31` } },
    { key: 'all', label: 'All time', range: { from: weekStartOf(weeks[0].weekEnding), to: weeks[weeks.length - 1].weekEnding.slice(0, 10) } },
  ];
}

/** How an expected week was worked out, in words. */
function sourceOf(w: TimelineWeek, key: string, today: string) {
  if (w.status === 'actual') return { short: 'Actual', long: 'From the statement, or the weeks you imported' };
  if (w.status === 'forecast') {
    const pct = `${w.growthPercent ?? 20}%`;
    if (w.basis === 'recent') return { short: 'Expected', long: `No numbers from last year, so the latest actuals + ${pct}` };
    if (w.basis === 'last_year_week') return { short: 'Expected', long: `The same week last year + ${pct}, spread over the days the way recent weeks went` };
    if (w.basis === 'last_year_days') return { short: 'Expected', long: `The same days last year + ${pct}` };
    return { short: 'Expected', long: `Worked out from last year, or the latest actuals, + ${pct}` };
  }
  return { short: key < today ? 'No data' : '—', long: key < today ? 'No statement or imported numbers for this week' : 'Nothing to work it out from yet' };
}

const ScheduleTimeline: React.FC = () => {
  const navigate = useNavigate();
  const [weeks, setWeeks] = useState<TimelineWeek[] | null>(null);
  const [thisWeek, setThisWeek] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [metric, setMetric] = useState<Metric>('stops');
  const [grain, setGrain] = useState<Grain>('week');
  const [range, setRange] = useState<DateRange | null>(null);

  useEffect(() => {
    ScheduleService.timeline()
      .then((body) => { setWeeks(body.weeks); setThisWeek(body.thisWeek.slice(0, 10)); })
      .catch((err) => { setError(describeError(err, 'Could not load the timeline.')); setWeeks([]); });
  }, []);

  const presets = useMemo(() => timelinePresets(thisWeek, weeks || []), [thisWeek, weeks]);
  const defaultRange = presets.find((p) => p.key === DEFAULT_PRESET)?.range || null;
  useEffect(() => { setRange((r) => r || defaultRange); }, [defaultRange]);
  const filtered = metric !== 'stops' || grain !== 'week'
    || (!!range && !!defaultRange && (range.from !== defaultRange.from || range.to !== defaultRange.to));
  const reset = () => { setMetric('stops'); setGrain('week'); setRange(defaultRange); };

  // A week counts when any of its days is in the range.
  const inRange = useMemo(() => (weeks || []).filter((w) => !range
    || (w.weekEnding.slice(0, 10) >= range.from && weekStartOf(w.weekEnding) <= range.to)), [weeks, range]);

  const max = metric === 'stops' ? 'maxStops' : 'maxPackages';
  const name = metric === 'stops' ? 'Stops' : 'Packages';

  const chart = useMemo(() => {
    if (grain === 'week') {
      return {
        slots: inRange.map((w): LineWeek => ({ key: w.weekEnding.slice(0, 10), label: shortDate(weekStartOf(w.weekEnding)) })),
        actual: inRange.map((w) => (w.status === 'actual' ? w[metric] : null)),
        forecast: inRange.map((w) => (w.status === 'forecast' ? w[metric] : null)),
        capacity: inRange.map((w) => w[max]),
      };
    }
    const days = inRange
      .flatMap((w) => w.days.map((d) => ({ ...d, status: w.status })))
      .filter((d) => !range || (d.date.slice(0, 10) >= range.from && d.date.slice(0, 10) <= range.to));
    return {
      slots: days.map((d): LineWeek => ({ key: d.date.slice(0, 10), label: `${weekdayShort(d.date)} ${shortDate(d.date)}` })),
      actual: days.map((d) => (d.status === 'actual' ? d[metric] : null)),
      forecast: days.map((d) => (d.status === 'forecast' ? d[metric] : null)),
      capacity: days.map((d) => d[max]),
    };
  }, [inRange, range, grain, metric, max]);

  const rows = useMemo(
    () => inRange.filter((w) => w.status !== 'none' || w.maxStops !== null).slice().reverse(),
    [inRange],
  );
  // The list can run far into the future and the past; start at now.
  const current = useRef<HTMLTableRowElement>(null);
  useEffect(() => { current.current?.scrollIntoView?.({ block: 'center' }); }, [rows]);

  const open = (key: string) => navigate(`/schedule?week=${key}`);
  // A day on the chart opens the week it is in.
  const openDay = (key: string) => open(weekEndingFor(new Date(`${key}T12:00:00`)));
  const today = useMemo(() => isoDay(new Date()), []);

  const left = (w: TimelineWeek, key: Metric) => {
    const cap = key === 'stops' ? w.maxStops : w.maxPackages;
    const v = w[key];
    return cap === null || v === null ? null : cap - v;
  };

  return (
    <PageBody fluid>
      <SchedulePanel />
      <PageHeader title="Timeline" subtitle="Actual volume, what is expected next, and what the schedule can carry.">
        <FilterBar onReset={reset} canReset={filtered}>
          <DateRangeFilter
            range={range}
            presets={presets}
            onChange={setRange}
            defaultKey={DEFAULT_PRESET}
            disabled={!presets.length}
            describe={(r) => {
              const n = (weeks || []).filter((w) => w.weekEnding.slice(0, 10) >= r.from && weekStartOf(w.weekEnding) <= r.to).length;
              return `${n} ${n === 1 ? 'week' : 'weeks'} in this range`;
            }}
          />
          <ChoiceFilter<Metric>
            icon={<MeasureIcon />}
            label="Measure"
            value={metric}
            defaultValue="stops"
            onChange={setMetric}
            choices={[{ key: 'stops', label: 'Stops' }, { key: 'packages', label: 'Packages' }]}
          />
          <ChoiceFilter<Grain>
            icon={<GrainIcon />}
            label="By"
            value={grain}
            defaultValue="week"
            onChange={setGrain}
            choices={[{ key: 'week', label: 'Week' }, { key: 'day', label: 'Day', note: 'weeks with statements' }]}
          />
        </FilterBar>
      </PageHeader>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {weeks === null ? <LoadingState /> : !(weeks || []).some((w) => w.status !== 'none' || w.maxStops !== null) ? (
        <EmptyState title="Nothing to show yet" description="Each statement you upload adds to this. So does importing past weeks in Weekly volume." />
      ) : !rows.length ? (
        <EmptyState title="Nothing in this range" action={{ label: 'Clear filters', onClick: reset }} />
      ) : (
        <Stack gap={2}>
          {chart.slots.length > 1 ? (
            <LineChart
              zero
              title={`${name} a ${grain}`}
              weeks={chart.slots}
              series={[
                { series: 'actual', label: 'Actual', values: chart.actual },
                { series: 'forecast', label: 'Expected', values: chart.forecast },
                { series: 'capacity', label: 'Capacity', values: chart.capacity },
              ]}
              format={(n) => count(Math.round(n))}
              height={260}
              onPick={grain === 'week' ? open : openDay}
              slotName={(w) => (grain === 'week' ? `Week of ${w.label}` : w.label)}
            />
          ) : grain === 'day' && (
            <Alert severity="info">No days in this range. Days come from statements; the weeks before them were kept by the week.</Alert>
          )}

          <Card sx={{ overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: '60vh' }}>
              <Table size="small" stickyHeader sx={{ minWidth: 820 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ px: 1.25 }}>Week of</TableCell>
                    <TableCell sx={{ px: 1.25 }}>Source</TableCell>
                    {['Stops', 'Max stops', 'Stops left', 'Packages', 'Max pkgs', 'Pkgs left', 'Stops last yr', 'Pkgs last yr'].map((h) => (
                      <TableCell key={h} align="right" sx={{ px: 1.25 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((w) => {
                    const key = w.weekEnding.slice(0, 10);
                    const start = weekStartOf(key);
                    const stopsLeft = left(w, 'stops');
                    const packagesLeft = left(w, 'packages');
                    const source = sourceOf(w, key, today);
                    return (
                      <TableRow
                        key={key} hover tabIndex={0} sx={{ cursor: 'pointer' }}
                        ref={key === thisWeek ? current : undefined}
                        selected={key === thisWeek}
                        onClick={() => open(key)}
                        onKeyDown={(e) => { if (e.key === 'Enter') open(key); }}
                      >
                        <TableCell sx={num}>{shortDate(start)}, {start.slice(0, 4)}</TableCell>
                        <TableCell sx={{ ...num, color: 'text.secondary' }}>
                          <Tooltip title={source.long}><span>{source.short}</span></Tooltip>
                        </TableCell>
                        <TableCell align="right" sx={num}>{opt(w.stops)}</TableCell>
                        <TableCell align="right" sx={num}>{opt(w.maxStops)}</TableCell>
                        <TableCell align="right" sx={{ ...num, fontWeight: 700, color: stopsLeft !== null && stopsLeft < 0 ? 'error.main' : undefined }}>{opt(stopsLeft)}</TableCell>
                        <TableCell align="right" sx={num}>{opt(w.packages)}</TableCell>
                        <TableCell align="right" sx={num}>{opt(w.maxPackages)}</TableCell>
                        <TableCell align="right" sx={{ ...num, fontWeight: 700, color: packagesLeft !== null && packagesLeft < 0 ? 'error.main' : undefined }}>{opt(packagesLeft)}</TableCell>
                        <TableCell align="right" sx={{ ...num, color: 'text.secondary' }}>{opt(w.lastYear?.stops)}</TableCell>
                        <TableCell align="right" sx={{ ...num, color: 'text.secondary' }}>{opt(w.lastYear?.packages)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Stack>
      )}
    </PageBody>
  );
};

export default ScheduleTimeline;
